import { useEffect, useMemo, useState } from 'react';

import type { Organization } from '@/api/organizations';
import { Button, Select, StatePanel, StatusNotice } from '@/components/ui';

interface ConsoleScopeSwitcherProps {
  activeOrganizationId: number | null;
  activeOrganizationName: string | null;
  error: Error | null;
  isImpersonating: boolean;
  isLoading: boolean;
  isSwitching: boolean;
  organizations: Organization[];
  onEnterOrganization: (organization: Organization) => Promise<void>;
  onRetry: () => void;
  onReturnToPlatform: () => Promise<void>;
}

export default function ConsoleScopeSwitcher({
  activeOrganizationId,
  activeOrganizationName,
  error,
  isImpersonating,
  isLoading,
  isSwitching,
  organizations,
  onEnterOrganization,
  onRetry,
  onReturnToPlatform,
}: ConsoleScopeSwitcherProps) {
  const tenantOrganizations = useMemo(
    () => organizations.filter((organization) => !organization.is_platform),
    [organizations],
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');

  useEffect(() => {
    if (!selectedOrganizationId && tenantOrganizations.length > 0) {
      setSelectedOrganizationId(String(tenantOrganizations[0].id));
    }
  }, [selectedOrganizationId, tenantOrganizations]);

  const selectedOrganization = tenantOrganizations.find(
    (organization) => String(organization.id) === selectedOrganizationId,
  );

  return (
    <div className="grid gap-5">
      {isImpersonating ? (
        <StatusNotice
          state="ready"
          title="Organization scope is active"
          description={
            <div className="grid gap-2">
              <p>
                Requests and cached console reads are scoped to{' '}
                <strong>
                  {activeOrganizationName ??
                    (activeOrganizationId ? `Organization ${activeOrganizationId}` : 'the selected organization')}
                </strong>.
              </p>
              <p className="text-meta">
                Platform pages stay unmounted until you return to platform scope.
              </p>
            </div>
          }
          action={
            <Button
              variant="caution"
              isLoading={isSwitching}
              onClick={() => void onReturnToPlatform()}
            >
              Return to platform scope
            </Button>
          }
        />
      ) : (
        <StatusNotice
          state="ready"
          title="Platform scope is active"
          description="You're working across all organizations. Pick one to see its devices."
        />
      )}

      {isLoading ? (
        <StatePanel
          state="loading"
          objectName="organizations"
          taskContext="Choose an organization scope"
        />
      ) : error ? (
        <StatePanel
          state="failed"
          failedAction="Load organizations"
          outcome={error.message || 'The organizations API returned a non-success result.'}
          safeRetryBoundary="This was a read-only request; retry does not repeat a mutation."
          recovery={{
            kind: 'retry',
            safeToRetry: true,
            onAction: onRetry,
          }}
        />
      ) : tenantOrganizations.length === 0 ? (
        <StatePanel
          state="empty"
          objectNamePlural="tenant organizations"
          objectNameSingular="tenant organization"
          activeScope="Platform organization directory"
        />
      ) : (
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (selectedOrganization) void onEnterOrganization(selectedOrganization);
          }}
        >
          <Select
            label="Organization to enter"
            description="The selected tenant becomes the explicit scope for API requests and organization navigation."
            value={selectedOrganizationId}
            onChange={(event) => setSelectedOrganizationId(event.target.value)}
          >
            {tenantOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name || `Organization ${organization.id}`}
              </option>
            ))}
          </Select>
          <Button
            type="submit"
            variant="primary"
            disabled={!selectedOrganization}
            isLoading={isSwitching}
          >
            Enter organization workspace
          </Button>
        </form>
      )}
    </div>
  );
}
